import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Plus, Search, Filter, Clock, AlertTriangle, CheckCircle2, 
  User, Calendar, FileText, Send, Loader2, ChevronRight,
  LayoutList, LayoutGrid, Sparkles, Bell, XCircle, ArrowRight,
  Link2, Copy, ExternalLink
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

// Status configuration (UPDATED)
const STATUS_CONFIG = {
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700', icon: Send },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700', icon: Clock },
  blocked: { label: 'Blocked', color: 'bg-red-100 text-red-700', icon: XCircle },
  on_hold: { label: 'On Hold', color: 'bg-orange-100 text-orange-700', icon: Clock },
  waiting_on_requestor: { label: 'Waiting on Requestor', color: 'bg-purple-100 text-purple-700', icon: User },
  in_review: { label: 'In Review', color: 'bg-cyan-100 text-cyan-700', icon: FileText },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-700', icon: XCircle }
};

const PRIORITY_CONFIG = {
  urgent: { label: 'Urgent (72h)', color: 'bg-red-100 text-red-700' },
  high: { label: 'High (5d)', color: 'bg-orange-100 text-orange-700' },
  standard: { label: 'Standard (7d)', color: 'bg-blue-100 text-blue-700' },
  low: { label: 'Low (10d)', color: 'bg-slate-100 text-slate-700' }
};

// Request Card Component
const RequestCard = ({ request, onClick, canManage }) => {
  const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.submitted;
  const priorityConfig = PRIORITY_CONFIG[request.priority] || PRIORITY_CONFIG.standard;
  const StatusIcon = statusConfig.icon;

  return (
    <Card 
      className="mcc-card hover:shadow-md transition-all cursor-pointer border-l-4"
      style={{ borderLeftColor: request.is_sla_breached ? '#ef4444' : request.sla_hours_remaining && request.sla_hours_remaining < 24 ? '#f59e0b' : 'transparent' }}
      onClick={onClick}
      data-testid={`request-card-${request.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 line-clamp-1">{request.title}</h3>
            <p className="text-sm text-slate-500 mt-1">{request.request_type}</p>
          </div>
          <Badge className={priorityConfig.color}>{priorityConfig.label}</Badge>
        </div>

        <p className="text-sm text-slate-600 line-clamp-2 mb-3">{request.description}</p>

        <div className="flex items-center gap-2 flex-wrap mb-3">
          <Badge className={statusConfig.color} variant="secondary">
            <StatusIcon className="w-3 h-3 mr-1" />
            {statusConfig.label}
          </Badge>
          <Badge variant="outline">{request.business_unit}</Badge>
        </div>

        <div className="flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <User className="w-3 h-3" />
            <span>{request.primary_assignee_name || request.assigned_to_name || 'Unassigned'}</span>
          </div>
          <div className="flex items-center gap-1">
            {request.is_sla_breached ? (
              <span className="text-red-600 font-medium flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                SLA Breached
              </span>
            ) : request.sla_hours_remaining && request.sla_hours_remaining < 24 ? (
              <span className="text-amber-600 font-medium">
                {Math.round(request.sla_hours_remaining)}h remaining
              </span>
            ) : (
              <span>{format(parseISO(request.created_at), 'MMM d')}</span>
            )}
          </div>
        </div>

        {request.ai_triage && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-1 text-xs text-purple-600">
              <Sparkles className="w-3 h-3" />
              <span>AI suggests: {request.ai_triage.suggested_assignee_name || 'No assignee'} • ~{request.ai_triage.estimated_hours}h</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// New Request Form Component - Dynamic with conditional sections
const NewRequestForm = ({ config, projects = [], onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    // Section 1: Always visible
    title: '',
    business_unit: '',
    is_partner: false,
    partner_name: '',
    marketing_support_type: '',
    first_name: '',
    email: '',
    phone: '',
    priority: 'standard',
    description: '',
    related_project_id: '',
    // Dynamic form data
    form_data: {
      creative: {},
      website: {},
      events: {},
      social_media: {},
      email: {}
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();

  // Auto-populate user info
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        first_name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // Prevent double-clicks
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        request_type: formData.marketing_support_type,
        related_project_id: formData.related_project_id || null
      };
      await onSubmit(payload);
      onClose();
    } catch (error) {
      console.error('Failed to submit request:', error);
      toast.error('Failed to submit request. Please try again.');
      setSubmitting(false);
    }
  };

  const updateFormData = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      form_data: {
        ...prev.form_data,
        [section]: {
          ...prev.form_data[section],
          [field]: value
        }
      }
    }));
  };

  const togglePlatform = (platform) => {
    const current = formData.form_data.social_media.platforms || [];
    const updated = current.includes(platform)
      ? current.filter(p => p !== platform)
      : [...current, platform];
    updateFormData('social_media', 'platforms', updated);
  };

  const toggleEventAsset = (asset) => {
    const current = formData.form_data.events.asset_needs || [];
    const updated = current.includes(asset)
      ? current.filter(a => a !== asset)
      : [...current, asset];
    updateFormData('events', 'asset_needs', updated);
  };

  const renderTypeSpecificSection = () => {
    switch (formData.marketing_support_type) {
      case 'Creative':
        return (
          <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Creative Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Asset Type *</Label>
                <Select value={formData.form_data.creative.asset_type || ''} onValueChange={(v) => updateFormData('creative', 'asset_type', v)}>
                  <SelectTrigger><SelectValue placeholder="Select asset type" /></SelectTrigger>
                  <SelectContent>
                    {(config.creative_asset_types || ['Social Media Graphics', 'Email Banner', 'Flyer/Poster', 'Presentation', 'Infographic', 'Other']).map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Usage Type</Label>
                <Select value={formData.form_data.creative.usage_type || ''} onValueChange={(v) => updateFormData('creative', 'usage_type', v)}>
                  <SelectTrigger><SelectValue placeholder="Select usage" /></SelectTrigger>
                  <SelectContent>
                    {(config.creative_usage_types || ['Digital Only', 'Print Only', 'Both', 'Internal Use']).map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Goals / Requirements *</Label>
                <Textarea value={formData.form_data.creative.goals_requirements || ''} onChange={(e) => updateFormData('creative', 'goals_requirements', e.target.value)} placeholder="What do you want to achieve with this creative?" rows={3} />
              </div>
              <div className="md:col-span-2">
                <Label>Design Specs</Label>
                <Textarea value={formData.form_data.creative.design_specs || ''} onChange={(e) => updateFormData('creative', 'design_specs', e.target.value)} placeholder="Dimensions, colors, fonts, etc." rows={2} />
              </div>
              <div className="md:col-span-2">
                <Label>Messaging / Copy</Label>
                <Textarea value={formData.form_data.creative.messaging || ''} onChange={(e) => updateFormData('creative', 'messaging', e.target.value)} placeholder="Text/copy to include" rows={2} />
              </div>
              <div>
                <Label>Deadline</Label>
                <Input type="date" value={formData.form_data.creative.deadline || ''} onChange={(e) => updateFormData('creative', 'deadline', e.target.value)} />
              </div>
            </div>
          </div>
        );

      case 'Website':
        return (
          <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Website Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Work Type *</Label>
                <Select value={formData.form_data.website.work_type || ''} onValueChange={(v) => updateFormData('website', 'work_type', v)}>
                  <SelectTrigger><SelectValue placeholder="Select work type" /></SelectTrigger>
                  <SelectContent>
                    {(config.website_work_types || ['New Page', 'Page Update', 'Bug Fix', 'Content Update', 'Design Change', 'Other']).map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>URL / Page</Label>
                <Input value={formData.form_data.website.url_page || ''} onChange={(e) => updateFormData('website', 'url_page', e.target.value)} placeholder="https://..." />
              </div>
              <div className="md:col-span-2">
                <Label>Updates Needed *</Label>
                <Textarea value={formData.form_data.website.updates_needed || ''} onChange={(e) => updateFormData('website', 'updates_needed', e.target.value)} placeholder="Describe the changes needed" rows={3} />
              </div>
              <div className="flex items-center gap-4">
                <Checkbox checked={formData.form_data.website.copy_ready || false} onCheckedChange={(v) => updateFormData('website', 'copy_ready', v)} />
                <Label>Copy is ready</Label>
              </div>
              <div className="flex items-center gap-4">
                <Checkbox checked={formData.form_data.website.requires_creative || false} onCheckedChange={(v) => updateFormData('website', 'requires_creative', v)} />
                <Label>Requires Creative Support</Label>
              </div>
              {formData.form_data.website.copy_ready && (
                <div className="md:col-span-2">
                  <Label>Copy Content</Label>
                  <Textarea value={formData.form_data.website.copy_content || ''} onChange={(e) => updateFormData('website', 'copy_content', e.target.value)} placeholder="Paste your copy here" rows={4} />
                </div>
              )}
              <div>
                <Label>Deadline</Label>
                <Input type="date" value={formData.form_data.website.deadline || ''} onChange={(e) => updateFormData('website', 'deadline', e.target.value)} />
              </div>
            </div>
          </div>
        );

      case 'Events':
        return (
          <div className="space-y-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <h3 className="font-semibold text-amber-800 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Event Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Event Name *</Label>
                <Input value={formData.form_data.events.event_name || ''} onChange={(e) => updateFormData('events', 'event_name', e.target.value)} placeholder="Name of the event" />
              </div>
              <div>
                <Label>Event Date *</Label>
                <Input type="date" value={formData.form_data.events.event_date || ''} onChange={(e) => updateFormData('events', 'event_date', e.target.value)} />
              </div>
              <div>
                <Label>Venue</Label>
                <Input value={formData.form_data.events.venue || ''} onChange={(e) => updateFormData('events', 'venue', e.target.value)} placeholder="Venue name" />
              </div>
              <div>
                <Label>Venue Address</Label>
                <Input value={formData.form_data.events.venue_address || ''} onChange={(e) => updateFormData('events', 'venue_address', e.target.value)} placeholder="Full address" />
              </div>
              <div>
                <Label>Number of Guests</Label>
                <Input type="number" value={formData.form_data.events.number_of_guests || ''} onChange={(e) => updateFormData('events', 'number_of_guests', parseInt(e.target.value))} />
              </div>
              <div>
                <Label>PH Attendees</Label>
                <Input value={formData.form_data.events.ph_attendees || ''} onChange={(e) => updateFormData('events', 'ph_attendees', e.target.value)} placeholder="Names of attending staff" />
              </div>
              <div className="md:col-span-2">
                <Label>Messaging</Label>
                <Textarea value={formData.form_data.events.messaging || ''} onChange={(e) => updateFormData('events', 'messaging', e.target.value)} placeholder="Key messaging for event" rows={2} />
              </div>
              <div>
                <Label>Deliverables Deadline</Label>
                <Input type="date" value={formData.form_data.events.deliverables_deadline || ''} onChange={(e) => updateFormData('events', 'deliverables_deadline', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <Label>Asset Needs (Select all that apply)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(config.event_asset_types || ['Email Invitation', 'Social Media Posts', 'Landing Page', 'Flyer/Poster', 'Signage', 'Presentation', 'Other']).map(asset => (
                    <Button key={asset} type="button" variant={(formData.form_data.events.asset_needs || []).includes(asset) ? 'default' : 'outline'} size="sm" onClick={() => toggleEventAsset(asset)}>
                      {asset}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'Social Media':
        return (
          <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 flex items-center gap-2">
              <Send className="w-4 h-4" /> Social Media Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Platforms * (Select all that apply)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(config.social_platforms || ['Facebook', 'Instagram', 'LinkedIn', 'Twitter/X', 'TikTok', 'YouTube']).map(platform => (
                    <Button key={platform} type="button" variant={(formData.form_data.social_media.platforms || []).includes(platform) ? 'default' : 'outline'} size="sm" onClick={() => togglePlatform(platform)}>
                      {platform}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Content Type</Label>
                <Select value={formData.form_data.social_media.content_type || ''} onValueChange={(v) => updateFormData('social_media', 'content_type', v)}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {['Single Post', 'Carousel', 'Story', 'Reel/Video', 'Series/Campaign'].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Goal</Label>
                <Input value={formData.form_data.social_media.goal || ''} onChange={(e) => updateFormData('social_media', 'goal', e.target.value)} placeholder="Awareness, engagement, etc." />
              </div>
              <div className="md:col-span-2">
                <Label>Message / Copy</Label>
                <Textarea value={formData.form_data.social_media.message || ''} onChange={(e) => updateFormData('social_media', 'message', e.target.value)} placeholder="What do you want to say?" rows={3} />
              </div>
              <div>
                <Label>Call to Action</Label>
                <Input value={formData.form_data.social_media.cta || ''} onChange={(e) => updateFormData('social_media', 'cta', e.target.value)} placeholder="Learn more, Sign up, etc." />
              </div>
              <div>
                <Label>Deadline</Label>
                <Input type="date" value={formData.form_data.social_media.deadline || ''} onChange={(e) => updateFormData('social_media', 'deadline', e.target.value)} />
              </div>
              <div className="flex items-center gap-4">
                <Checkbox checked={formData.form_data.social_media.requires_creative || false} onCheckedChange={(v) => updateFormData('social_media', 'requires_creative', v)} />
                <Label>Requires Creative Support</Label>
              </div>
            </div>
          </div>
        );

      case 'Email':
        return (
          <div className="space-y-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <h3 className="font-semibold text-indigo-800 flex items-center gap-2">
              <Send className="w-4 h-4" /> Email Campaign Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Campaign Name *</Label>
                <Input value={formData.form_data.email.campaign_name || ''} onChange={(e) => updateFormData('email', 'campaign_name', e.target.value)} placeholder="Campaign name" />
              </div>
              <div>
                <Label>Objective</Label>
                <Input value={formData.form_data.email.objective || ''} onChange={(e) => updateFormData('email', 'objective', e.target.value)} placeholder="Goal of the email" />
              </div>
              <div className="md:col-span-2">
                <Label>Audience / Segmentation</Label>
                <Textarea value={formData.form_data.email.audience_segmentation || ''} onChange={(e) => updateFormData('email', 'audience_segmentation', e.target.value)} placeholder="Who should receive this?" rows={2} />
              </div>
              <div className="md:col-span-2">
                <Label>Messaging / Content</Label>
                <Textarea value={formData.form_data.email.messaging || ''} onChange={(e) => updateFormData('email', 'messaging', e.target.value)} placeholder="Key messages and content" rows={3} />
              </div>
              <div>
                <Label>Call to Action</Label>
                <Input value={formData.form_data.email.cta || ''} onChange={(e) => updateFormData('email', 'cta', e.target.value)} placeholder="Primary CTA" />
              </div>
              <div>
                <Label>Links & Assets</Label>
                <Input value={formData.form_data.email.links_assets || ''} onChange={(e) => updateFormData('email', 'links_assets', e.target.value)} placeholder="URLs, attachments, etc." />
              </div>
              <div>
                <Label>Timeline / Cadence</Label>
                <Input value={formData.form_data.email.timeline_cadence || ''} onChange={(e) => updateFormData('email', 'timeline_cadence', e.target.value)} placeholder="When and how often?" />
              </div>
              <div>
                <Label>Success Metrics</Label>
                <Input value={formData.form_data.email.success_metrics || ''} onChange={(e) => updateFormData('email', 'success_metrics', e.target.value)} placeholder="How will you measure success?" />
              </div>
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea value={formData.form_data.email.notes || ''} onChange={(e) => updateFormData('email', 'notes', e.target.value)} placeholder="Any additional notes" rows={2} />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Section 1: Always Visible */}
      <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
        <h3 className="font-semibold text-slate-800">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor="title">Request Name *</Label>
            <Input id="title" value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} placeholder="Brief description of what you need" required data-testid="request-title-input" />
          </div>

          <div>
            <Label>Business Unit *</Label>
            <Select value={formData.business_unit} onValueChange={(v) => setFormData(prev => ({ ...prev, business_unit: v }))}>
              <SelectTrigger data-testid="business-unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
              <SelectContent>
                {config.business_units?.map(unit => (
                  <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Marketing Support Type *</Label>
            <Select value={formData.marketing_support_type} onValueChange={(v) => setFormData(prev => ({ ...prev, marketing_support_type: v }))}>
              <SelectTrigger data-testid="support-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {(config.marketing_support_types || config.request_types)?.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-4">
            <Checkbox id="is_partner" checked={formData.is_partner} onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_partner: v }))} />
            <Label htmlFor="is_partner" className="cursor-pointer">Is this for a Partner?</Label>
          </div>

          {formData.is_partner && (
            <div>
              <Label>Partner Name</Label>
              <Input value={formData.partner_name} onChange={(e) => setFormData(prev => ({ ...prev, partner_name: e.target.value }))} placeholder="Partner organization name" />
            </div>
          )}

          <div>
            <Label>Priority</Label>
            <Select value={formData.priority} onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}>
              <SelectTrigger><SelectValue placeholder="Select priority" /></SelectTrigger>
              <SelectContent>
                {config.priorities?.map(p => (
                  <SelectItem key={p} value={p}>{PRIORITY_CONFIG[p]?.label || p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Your Name</Label>
            <Input value={formData.first_name} onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))} placeholder="Your name" />
          </div>

          <div>
            <Label>Email</Label>
            <Input type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} placeholder="your@email.com" />
          </div>

          <div>
            <Label>Phone (Optional)</Label>
            <Input value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} placeholder="Phone number" />
          </div>

          {/* Link to Project */}
          <div>
            <Label>Link to Project</Label>
            <Select value={formData.related_project_id || "none"} onValueChange={(v) => setFormData(prev => ({ ...prev, related_project_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="request-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Project</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <Label>Description *</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} placeholder="Provide details about your request..." rows={3} required />
          </div>
        </div>
      </div>

      {/* Conditional Sections based on marketing_support_type */}
      {renderTypeSpecificSection()}

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={submitting || !formData.title || !formData.marketing_support_type || !formData.business_unit}>
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <><Send className="w-4 h-4 mr-2" /> Submit Request</>}
        </Button>
      </div>
    </form>
  );
};

// Main Requests Page
const RequestsPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [config, setConfig] = useState({});
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [shareLinkDialogOpen, setShareLinkDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [view, setView] = useState('list');

  const canManage = ['admin', 'manager', 'contributor'].includes(user?.role);
  
  // Generate the public request form URL
  const publicFormUrl = `${window.location.origin}/submit-request`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(publicFormUrl);
    toast.success('Link copied to clipboard!');
  };

  const fetchData = useCallback(async () => {
    try {
      const [requestsRes, configRes, projectsRes] = await Promise.all([
        api.get('/requests'),
        api.get('/requests/config'),
        api.get('/projects')
      ]);
      setRequests(requestsRes.data);
      setConfig(configRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitRequest = async (data) => {
    await api.post('/requests', data);
    toast.success('Request submitted successfully!');
    fetchData();
  };

  const filteredRequests = requests.filter(req => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!req.title.toLowerCase().includes(query) && 
          !req.description.toLowerCase().includes(query) &&
          !req.request_type.toLowerCase().includes(query)) {
        return false;
      }
    }
    if (statusFilter !== 'all' && req.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && req.priority !== priorityFilter) return false;
    return true;
  });

  // Group requests by status for Kanban view
  const groupedRequests = config.statuses?.reduce((acc, status) => {
    acc[status] = filteredRequests.filter(r => r.status === status);
    return acc;
  }, {}) || {};

  // Stats
  const openRequests = requests.filter(r => !['completed', 'closed_not_moving_forward'].includes(r.status));
  const breachedRequests = requests.filter(r => r.is_sla_breached);
  const atRiskRequests = requests.filter(r => r.sla_hours_remaining && r.sla_hours_remaining < 24 && r.sla_hours_remaining > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="requests-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Marketing Requests</h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">{requests.length} total requests</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          {/* Share Public Link Button */}
          <Dialog open={shareLinkDialogOpen} onOpenChange={setShareLinkDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2 w-full sm:w-auto" data-testid="share-public-link-btn">
                <Link2 className="w-4 h-4" />
                <span className="hidden sm:inline">Public Form</span>
                <span className="sm:hidden">Share Link</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-blue-600" />
                  Public Request Form Link
                </DialogTitle>
                <DialogDescription>
                  Share this link with anyone who needs to submit a marketing request. No login required.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-2">
                  <Input 
                    value={publicFormUrl} 
                    readOnly 
                    className="flex-1 bg-slate-50 font-mono text-sm"
                    data-testid="public-form-url"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={copyToClipboard}
                    data-testid="copy-link-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1 gap-2"
                    onClick={copyToClipboard}
                  >
                    <Copy className="w-4 h-4" />
                    Copy Link
                  </Button>
                  <Button 
                    className="flex-1 gap-2"
                    onClick={() => window.open(publicFormUrl, '_blank')}
                    data-testid="open-public-form-btn"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Form
                  </Button>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Tip:</strong> Add this link to your company intranet, Slack, or email signature so team members can easily submit requests.
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* New Request Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-blue-600 hover:bg-blue-700 w-full sm:w-auto" data-testid="new-request-btn">
                <Plus className="w-4 h-4" />
                New Request
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Submit Marketing Request</DialogTitle>
              </DialogHeader>
              <NewRequestForm config={config} projects={projects} onSubmit={handleSubmitRequest} onClose={() => setDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3">
        <Card className="mcc-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-blue-50 rounded-lg shrink-0">
                <FileText className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-500 uppercase truncate">Open</p>
                <p className="text-lg md:text-xl font-bold text-slate-900">{openRequests.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mcc-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-red-50 rounded-lg shrink-0">
                <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-red-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-500 uppercase truncate">Breached</p>
                <p className="text-lg md:text-xl font-bold text-red-600">{breachedRequests.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mcc-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-amber-50 rounded-lg shrink-0">
                <Clock className="w-4 h-4 md:w-5 md:h-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-500 uppercase truncate">At Risk</p>
                <p className="text-lg md:text-xl font-bold text-amber-600">{atRiskRequests.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mcc-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-emerald-50 rounded-lg shrink-0">
                <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-500 uppercase truncate">Done</p>
                <p className="text-lg md:text-xl font-bold text-emerald-600">
                  {requests.filter(r => r.status === 'completed').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search requests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full sm:w-64"
              data-testid="search-requests"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40" data-testid="filter-status">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {config.statuses?.map(status => (
                <SelectItem key={status} value={status}>
                  {STATUS_CONFIG[status]?.label || status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-full sm:w-40" data-testid="filter-priority">
              <SelectValue placeholder="All Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              {config.priorities?.map(p => (
                <SelectItem key={p} value={p}>{PRIORITY_CONFIG[p]?.label || p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg w-fit">
          <Button
            variant={view === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setView('list')}
          >
            <LayoutList className="w-4 h-4" />
          </Button>
          <Button
            variant={view === 'kanban' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setView('kanban')}
          >
            <LayoutGrid className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* List View */}
      {view === 'list' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRequests.map(request => (
            <RequestCard
              key={request.id}
              request={request}
              canManage={canManage}
              onClick={() => navigate(`/requests/${request.id}`)}
            />
          ))}
          {filteredRequests.length === 0 && (
            <Card className="mcc-card md:col-span-2 lg:col-span-3">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No requests found</h3>
                <p className="text-slate-500 mb-4">Submit your first marketing request to get started</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Kanban View */}
      {view === 'kanban' && (
        <div className="kanban-board overflow-x-auto -mx-3 px-3">
          {config.statuses?.filter(s => !['closed_not_moving_forward'].includes(s)).map(status => {
            const statusRequests = groupedRequests[status] || [];
            const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.submitted;
            
            return (
              <div key={status} className="kanban-column bg-slate-50 rounded-xl p-3 min-w-[300px]">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                    <span className="text-sm text-slate-500">{statusRequests.length}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  {statusRequests.map(request => (
                    <RequestCard
                      key={request.id}
                      request={request}
                      canManage={canManage}
                      onClick={() => navigate(`/requests/${request.id}`)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RequestsPage;
